import React, { useState } from "react";
import { motionSafe as motion } from "@/lib/motionSafe";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Mail, MapPin, Phone, Calendar, ArrowUpRight } from "lucide-react";
import { useI18n } from "../../i18n";
import { ConsentCheckboxes } from "./ConsentCheckboxes";
import { BOOKING_URL } from "../../lib/booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export const Contact = () => {
  const { lang, t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [consentProcessing, setConsentProcessing] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = true;
    if (!emailValid(form.email)) errs.email = true;
    setErrors(errs);
    if (!consentProcessing) {
      setShowConsentError(true);
    }
    if (Object.keys(errs).length || !consentProcessing) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads/contact`, {
        ...form,
        locale: lang,
        consent_processing: consentProcessing,
        consent_marketing: consentMarketing,
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", message: "" });
      setConsentProcessing(false);
      setConsentMarketing(false);
      setShowConsentError(false);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-white/5 border-b border-white/20 rounded-none px-3 py-3 text-white placeholder:text-gray-400 focus:outline-none focus:border-[#0052FF] transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36 bg-[#080F1D] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#3B82F6] mb-6">{t.contact.overline}</div>
          <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-5xl leading-[1.03] text-white">{t.contact.title}</h2>
          <p className="mt-6 text-gray-400 text-lg max-w-md">{t.contact.sub}</p>
          <div className="mt-10 space-y-4 font-mono-tech text-sm text-gray-400">
            <a href="mailto:michael.jesse@nextech.law" className="flex items-center gap-3 hover:text-white transition-colors" data-testid="contact-email-link"><Mail size={16} className="text-[#3B82F6]" /> michael.jesse@nextech.law</a>
            <a href="tel:+436641315687" className="flex items-center gap-3 hover:text-white transition-colors" data-testid="contact-phone-link"><Phone size={16} className="text-[#3B82F6]" /> +43 664 1315687</a>
            <div className="flex items-center gap-3"><MapPin size={16} className="text-[#3B82F6]" /> EU · Remote-first</div>
          </div>

          {/* Direct booking — Google Calendar Appointment Schedule (Meet auto-invite) */}
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-booking-cta"
            className="group mt-10 relative block bg-gradient-to-br from-[#0052FF] to-[#2563EB] rounded-sm p-6 overflow-hidden hover:from-[#2563EB] hover:to-[#0052FF] transition-colors"
          >
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="relative flex items-start gap-4">
              <span className="shrink-0 w-11 h-11 rounded-sm bg-white/15 border border-white/25 flex items-center justify-center">
                <Calendar size={20} className="text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/80">
                  {lang === "de" ? "Direkt Termin buchen" : "Book directly"}
                </div>
                <div className="mt-1 font-display text-xl md:text-2xl font-extrabold text-white leading-tight">
                  {lang === "de" ? "15 Min Kennenlern-Call" : "15-min intro call"}
                </div>
                <div className="mt-1 text-sm text-white/85">
                  {lang === "de"
                    ? "Sofort verfügbare Slots · Google Meet-Einladung automatisch."
                    : "Available slots · Google Meet invite sent automatically."}
                </div>
              </div>
              <ArrowUpRight size={18} className="text-white shrink-0 mt-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </a>

          <div className="mt-6 flex items-center gap-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-gray-400">
            <span className="h-px flex-1 bg-white/10" />
            {lang === "de" ? "Oder schreiben Sie uns" : "Or drop us a message"}
            <span className="h-px flex-1 bg-white/10" />
          </div>
        </div>

        <motion.form
          data-testid="hubspot-contact-form"
          onSubmit={submit}
          initial={{ opacity: 1, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass rounded-sm p-6 md:p-8 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <input data-testid="contact-name" className={field} placeholder={t.contact.name}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {errors.name && <p className="text-xs text-red-400 mt-1">{t.assessment.emailError}</p>}
            </div>
            <input data-testid="contact-company" className={field} placeholder={t.contact.company}
              value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </div>
          <div>
            <input data-testid="contact-email" type="email" className={field} placeholder={t.contact.email}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            {errors.email && <p data-testid="contact-email-error" className="text-xs text-red-400 mt-1">{t.assessment.emailError}</p>}
          </div>
          <textarea data-testid="contact-message" rows={4} className={field + " resize-none"} placeholder={t.contact.message}
            value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />

          <ConsentCheckboxes
            processing={consentProcessing}
            marketing={consentMarketing}
            onProcessingChange={(v) => { setConsentProcessing(v); if (v) setShowConsentError(false); }}
            onMarketingChange={setConsentMarketing}
            showError={showConsentError}
            testIdPrefix="contact-consent"
          />

          <button data-testid="contact-submit" type="submit" disabled={loading}
            className="trace-btn w-full inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#2563EB] disabled:opacity-60 text-white font-medium px-7 py-4 rounded-sm transition-colors duration-200">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {loading ? t.contact.sending : t.contact.send}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
